import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Chip,
  ImageList,
  ImageListItem,
  Divider,
  Alert,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import PersonIcon from '@mui/icons-material/Person';
import HomeIcon from '@mui/icons-material/Home';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { AdminHomeDetail } from '../../../types/admin';
import { format } from 'date-fns';

interface HomeDetailModalProps {
  open: boolean;
  home: AdminHomeDetail | null;
  onClose: () => void;
}

/**
 * HomeDetailModal - Modal displaying full home details with image gallery
 *
 * Features:
 * - Property information
 * - Owner details
 * - Image gallery
 * - Flag status display
 */
export default function HomeDetailModal({
  open,
  home,
  onClose,
}: HomeDetailModalProps) {
  if (!home) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HomeIcon />
            {home.name}
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              label={home.status}
              color={home.status === 'active' ? 'success' : 'default'}
              size="small"
            />
            {home.flagged && (
              <Chip
                label="Flagged"
                color="error"
                size="small"
              />
            )}
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {home.flagged && home.flag_reason && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight="bold">
              Flagged: {home.flag_reason}
            </Typography>
            {home.flagged_at && (
              <Typography variant="caption" display="block">
                Flagged on {format(new Date(home.flagged_at), 'PPP')}
              </Typography>
            )}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Property Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Property Information
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HomeIcon fontSize="small" color="action" />
                <Typography variant="body2">
                  <strong>Address:</strong> {home.address || 'No address provided'}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarMonthIcon fontSize="small" color="action" />
                <Typography variant="body2">
                  <strong>Created:</strong> {format(new Date(home.created_at), 'PPP')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <ImageIcon fontSize="small" color="action" />
                <Typography variant="body2">
                  <strong>Images:</strong> {home.images_count} total
                </Typography>
              </Box>
            </Box>
            {home.description && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  {home.description}
                </Typography>
              </Box>
            )}
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Owner Information */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Owner Information
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {home.owner_picture && (
                <img
                  src={home.owner_picture}
                  alt={home.owner_name}
                  style={{ width: 48, height: 48, borderRadius: '50%' }}
                />
              )}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon fontSize="small" color="action" />
                  <Typography variant="body2">
                    <strong>{home.owner_name}</strong>
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {home.owner_email}
                </Typography>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Image Gallery */}
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Property Images ({home.images.length})
            </Typography>
            {home.images.length > 0 ? (
              <ImageList
                sx={{ maxHeight: 400 }}
                cols={3}
                rowHeight={200}
                gap={8}
              >
                {home.images.map((image, index) => (
                  <ImageListItem key={index}>
                    <img
                      src={image.url}
                      alt={image.caption || `Image ${index + 1}`}
                      loading="lazy"
                      style={{ objectFit: 'cover', height: '100%' }}
                    />
                  </ImageListItem>
                ))}
              </ImageList>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No images available
              </Typography>
            )}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} startIcon={<CloseIcon />}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
